import type { GameCapacity, JoinRole } from '@badger-sentinel/protocol';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ApiError, fetchCapacity, joinGame } from '../api/games.js';
import { activateDemo, isDemoCode } from '../demo/activate.js';
import { DEMO_CODE, DEMO_PASSPHRASE, isDemoRequested } from '../demo/config.js';
import { ImageSlot, preloadSlots, SLOT, SPLASH_CREDIT } from '../theme/imagery.js';
import { ErrorNote, inertUnless } from './chrome.js';
import type { JoinedSession } from './session.js';
import { sideOf, SIDE_ORDER } from './sides.js';

/**
 * Page 1 — Entry (docs/frontend/pages/entry.md), built against
 * `docs/frontend/page-files/Entry Screen Continuous.dc.html`.
 *
 * Splash → game code → side → four-word passphrase → display name, then hands
 * the session to the shell and stops having opinions.
 *
 * **These are stages, not a page.** The classification bar, the console header,
 * and the crest that entry closes on all belong to `LobbyScreen`, which keeps
 * them mounted straight through into seat select. That is the whole reason this
 * file draws no chrome and owns no handoff: the closing beat is a *continuous*
 * one, and a component that unmounts itself cannot animate into the next screen
 * — it can only cut to it.
 */

type Stage = 'code' | 'role' | 'auth' | 'name';

/**
 * The four stages in order, which is also the step counter the copy rules
 * presuppose ("STEP 2 / 3", `.claude/CLAUDE.md`) and the order browser Back
 * walks. A flow with four stages and no counter is one the player cannot place
 * themselves in.
 */
const STAGES: readonly Stage[] = ['code', 'role', 'auth', 'name'];

/** What each stage is, for the live region a screen reader hears. */
const STAGE_TITLE: Readonly<Record<Stage, string>> = {
  code: 'session code',
  role: 'select side',
  auth: 'cell passphrase',
  name: 'display name',
};

const EASE = 'cubic-bezier(.22,.61,.36,1)';
/** Side-select polls the unauthenticated capacity read while it's on screen. */
const CAPACITY_POLL_MS = 15_000;

export interface EntryStagesProps {
  readonly baseUrl: string;
  /** Fires the moment the player commits — the shell owns everything after it. */
  readonly onJoined: (session: JoinedSession) => void;
  /**
   * The shell is dissolving these stages into seat select. Nothing here changes
   * step while it is true — a form that rewinds itself on the way out reads as a
   * bug — it only lets the splash finish its push-in behind the fade.
   */
  readonly leaving?: boolean;
  /**
   * The typed code turned out to be the demo's, so the host now runs in this
   * tab. The shell has to hear about it: everything after entry — the roster,
   * the seat claim, the console's own socket — has to point at the worker
   * rather than at the API.
   */
  readonly onDemoActivated?: () => void;
}

export function EntryStages(props: EntryStagesProps): JSX.Element {
  const [stage, setStage] = useState<Stage>('code');

  const [codeCells, setCodeCellsState] = useState<string[]>(['', '', '', '']);
  const [codeError, setCodeError] = useState<ApiError | string | null>(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [capacity, setCapacity] = useState<GameCapacity | null>(null);
  const [role, setRole] = useState<JoinRole | null>(null);
  const roleRefs = useRef<Partial<Record<JoinRole, HTMLButtonElement | null>>>({});
  const [words, setWordsState] = useState<string[]>(['', '', '', '']);
  const wordRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [authError, setAuthError] = useState<ApiError | string | null>(null);
  const [checking, setChecking] = useState(false);

  /**
   * Set the moment `BSNT-DEMO` is accepted, and used in preference to the prop
   * from then on. Held locally rather than read back from the shell because the
   * call that follows activation must not race a re-render: the passphrase step
   * needs the demo's base URL on the very next request, not one render later.
   */
  const [demoBaseUrl, setDemoBaseUrl] = useState<string | null>(null);

  const [pendingJoin, setPendingJoin] = useState<JoinedSession | null>(null);
  const [displayName, setDisplayName] = useState('');
  const nameRef = useRef<HTMLInputElement | null>(null);
  const [splashFound, setSplashFound] = useState(false);

  const leaving = props.leaving === true;

  // The passphrase step and the display-name step live in the same panel and
  // slide past each other rather than swapping outright, so the panel's height
  // has to animate to whichever step is live — measured off that step's real
  // content, because an error note or the authenticating chip changes it.
  const authBodyRef = useRef<HTMLDivElement | null>(null);
  const nameBodyRef = useRef<HTMLDivElement | null>(null);
  const [bodyHeight, setBodyHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    // While leaving, the panel is fading out mid-step: re-measuring would resize
    // it under the fade, which is exactly the twitch this transition is avoiding.
    if (leaving) return;
    const el = stage === 'name' ? nameBodyRef.current : authBodyRef.current;
    if (el) setBodyHeight(el.scrollHeight);
  }, [stage, authError, checking, leaving]);

  // A `?code=BSNT-4K9P` deep link removes the most common typo of every session
  // (entry.md Open). The code is not a secret, so this is safe to read from the URL.
  //
  // `?demo` fills the same cells with the offline demo's fixed code, and the
  // passphrase step below fills itself the same way. The flow is not skipped:
  // entry is part of what the demo is showing, and a prefilled field is
  // self-evident where a bypassed screen would be a different product.
  useEffect(() => {
    let raw: string | null = null;
    try {
      const params = new URLSearchParams(window.location.search);
      raw = params.get('code') ?? (params.has('demo') ? DEMO_CODE : null);
    } catch {
      raw = null;
    }
    if (!raw) return;
    const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const withoutPrefix = clean.startsWith('BSNT') ? clean.slice(4) : clean;
    const chars = withoutPrefix.slice(0, 4).split('');
    if (chars.length > 0) setCodeCellsState([0, 1, 2, 3].map((k) => chars[k] ?? ''));
  }, []);

  const demoMode = isDemoRequested() || demoBaseUrl !== null;
  const baseUrl = demoBaseUrl ?? props.baseUrl;
  const fullCode = `BSNT-${codeCells.join('')}`;
  const codeComplete = codeCells.every((c) => c.length === 1);
  const wordsComplete = words.every((w) => w.trim().length > 0);

  // Side select polls capacity every 15s (docs/todo.md, "seat availability").
  useEffect(() => {
    if (stage !== 'role') return;
    const handle = setInterval(() => {
      fetchCapacity(baseUrl, fullCode)
        .then(setCapacity)
        .catch(() => undefined);
    }, CAPACITY_POLL_MS);
    return () => clearInterval(handle);
  }, [stage, baseUrl, fullCode]);

  // Warm every side crest and the auth-panel crest together, so the entrance
  // doesn't pay for three sequential image walks (theme/imagery.tsx).
  useEffect(() => {
    if (!capacity) return;
    preloadSlots(
      capacity.scenario,
      SIDE_ORDER.map((key) => sideOf(key, capacity.sides).crestSlot),
    );
  }, [capacity]);

  function setCodeCell(i: number, raw: string): void {
    setCodeError(null);
    const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    // >=3 unclean characters means a paste or a test harness's .fill() landed the
    // whole code in one cell — an ordinary overtype (old char + new char) is at
    // most 2. There's no maxLength on these inputs for exactly this reason: it
    // would clamp a paste down to one character before this handler ever saw it.
    if (clean.length >= 3) {
      const withoutPrefix = clean.startsWith('BSNT') ? clean.slice(4).replace(/^-/, '') : clean;
      const chars = withoutPrefix.slice(0, 4).split('');
      setCodeCellsState([0, 1, 2, 3].map((k) => chars[k] ?? codeCells[k] ?? ''));
      codeRefs.current[Math.max(Math.min(chars.length, 4) - 1, 0)]?.focus();
      return;
    }
    // A single keystroke, and what it *added* rather than the whole field value:
    // an overtype arrives as `old + new`, so `BSNT-`'s hyphen would otherwise
    // read as the character `T` being retyped.
    const previous = codeCells[i] ?? '';
    const added = previous !== '' && raw.startsWith(previous) ? raw.slice(previous.length) : raw;
    const typed = added.toUpperCase().replace(/[^A-Z0-9]/g, '');

    // **The prefix, typed rather than pasted.** `BSNT-` is printed beside these
    // cells, so a player who types the code exactly as issued fills them with
    // B S N T, and every character after that overwrote the last cell:
    // `BSNT-4K9P` landed as `BSNP`, four characters, `ENTER →` enabled, and
    // SESSION NOT FOUND. Deferring the strip to the *next* keystroke is what
    // makes it unambiguous — a code whose four characters really are BSNT is
    // finished at four and there is no next keystroke.
    if (i === 3 && added !== '' && codeCells.join('') === 'BSNT') {
      setCodeCellsState([typed, '', '', '']);
      codeRefs.current[typed === '' ? 0 : 1]?.focus();
      return;
    }

    // A hyphen or a space is not a character. It used to blank the cell.
    if (added !== '' && typed === '') return;

    const next = [...codeCells];
    next[i] = typed.slice(-1);
    setCodeCellsState(next);
    if (typed && i < 3) codeRefs.current[i + 1]?.focus();
  }

  function setWordCell(i: number, raw: string): void {
    setAuthError(null);
    const parts = raw
      .trim()
      .split(/[\s-]+/)
      .filter(Boolean);
    if (parts.length > 1) {
      // A paste of the whole phrase — real passphrases join with hyphens.
      setWordsState(
        [0, 1, 2, 3].map((k) => (parts[k] ?? words[k] ?? '').replace(/[^a-zA-Z]/g, '')),
      );
      wordRefs.current[Math.max(Math.min(parts.length, 4) - 1, 0)]?.focus();
      return;
    }
    const next = [...words];
    next[i] = raw.replace(/[^a-zA-Z]/g, '');
    setWordsState(next);
  }

  async function submitCode(): Promise<void> {
    if (codeChecking) return;
    if (!codeComplete) {
      setCodeError('CODE INCOMPLETE');
      return;
    }
    setCodeChecking(true);
    setCodeError(null);
    try {
      // `BSNT-DEMO` is a code, not a URL parameter, and somebody who was told it
      // will type it here. Recognised before the network is asked anything: the
      // live API has never heard of this game and would answer NOT FOUND, which
      // is a dead end that reads as a broken product rather than as a demo.
      let active = baseUrl;
      if (isDemoCode(fullCode) && demoBaseUrl === null) {
        active = await activateDemo();
        setDemoBaseUrl(active);
        props.onDemoActivated?.();
      }
      const cap = await fetchCapacity(active, fullCode);
      setCapacity(cap);
      setStage('role');
    } catch (error) {
      setCodeError(error instanceof ApiError ? error : 'REQUEST FAILED');
    } finally {
      setCodeChecking(false);
    }
  }

  function pickRole(key: JoinRole): void {
    setRole(key);
    setWordsState(demoMode ? DEMO_PASSPHRASE.split('-') : ['', '', '', '']);
    setAuthError(null);
    setStage('auth');
  }

  async function submitAuth(): Promise<void> {
    if (checking || !role) return;
    if (!wordsComplete) {
      setAuthError('PASSPHRASE INCOMPLETE');
      return;
    }
    setChecking(true);
    setAuthError(null);
    try {
      const password = words.map((w) => w.trim().toLowerCase()).join('-');
      const response = await joinGame(baseUrl, fullCode, role, password);
      setPendingJoin({
        sessionToken: response.sessionToken,
        gameId: response.gameId,
        code: response.code,
        role: response.role,
        displayName: null,
        seats: response.seats,
        scenario: response.scenario,
        sides: response.sides,
      });
      setStage('name');
    } catch (error) {
      setAuthError(error instanceof ApiError ? error : 'REQUEST FAILED');
    } finally {
      setChecking(false);
    }
  }

  /** One step back. `role` → `code` is a step, not an exit; the label says so. */
  function back(): void {
    if (stage === 'role') setStage('code');
    else if (stage === 'auth') setStage('role');
    else setStage('auth');
  }

  // Browser Back. Without an entry per stage the button left the SPA from any
  // point in a four-stage form, which on a phone is the same gesture as "go up
  // one level". `fromPop` stops the handler's own `setStage` from pushing the
  // entry it just consumed.
  const fromPop = useRef(false);
  useEffect(() => {
    if (fromPop.current) {
      fromPop.current = false;
      return;
    }
    const state = { bsStage: stage };
    if (stage === 'code') window.history.replaceState(state, '');
    else window.history.pushState(state, '');
  }, [stage]);

  useEffect(() => {
    function onPop(event: PopStateEvent): void {
      const target = (event.state as { bsStage?: unknown } | null)?.bsStage;
      if (typeof target !== 'string' || !STAGES.includes(target as Stage)) return;
      // A history entry can outlive the state that made it reachable — a forward
      // navigation to `name` after the session was dropped, say. Clamp rather
      // than render a step with nothing behind it.
      const wanted = target as Stage;
      const reachable =
        wanted === 'name' ? pendingJoin !== null : wanted === 'auth' ? role !== null : true;
      fromPop.current = true;
      setStage(reachable ? wanted : 'code');
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pendingJoin, role]);

  // Escape goes back one step from anywhere in a stage, not only from inside a
  // passphrase input — which was the only element that listened for it.
  useEffect(() => {
    if (stage === 'code' || leaving) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') back();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Focus follows the step. Back, Cancel and Escape used to leave it on a button
  // that had just become invisible, and there is nothing to tab to from there.
  const firstFocus = useRef(true);
  useEffect(() => {
    if (leaving) return;
    function focusStage(): void {
      const target =
        stage === 'code'
          ? codeRefs.current[0]
          : stage === 'role'
            ? (roleRefs.current[role ?? 'BLUFOR'] ?? roleRefs.current.BLUFOR)
            : stage === 'auth'
              ? wordRefs.current[0]
              : nameRef.current;
      target?.focus();
    }
    // The first paint takes focus now rather than in 60 ms: a deferred grab
    // races a player who has already started typing and pulls them back to the
    // first cell mid-code. Later stages keep the delay, because the panel they
    // are focusing into is still sliding in.
    if (firstFocus.current) {
      firstFocus.current = false;
      focusStage();
      return;
    }
    const handle = setTimeout(focusStage, 60);
    return () => clearTimeout(handle);
  }, [stage, leaving, role]);

  function enterLobby(): void {
    if (!pendingJoin || leaving) return;
    const trimmed = displayName.trim();
    props.onJoined({ ...pendingJoin, displayName: trimmed.length > 0 ? trimmed : null });
  }

  const stepNumber = STAGES.indexOf(stage) + 1;
  // The bucket's own number, off `x-ratelimit-remaining` (api/games.ts). The
  // copy rules allow a rule to be stated when it carries a number the player
  // needs now; this is that number, and it is the server's rather than a count
  // of this tab's own tries.
  const attemptsLeft =
    authError instanceof ApiError && (authError.status === 401 || authError.status === 429)
      ? (authError.attemptsLeft ?? null)
      : null;

  const picked = role ? sideOf(role, capacity?.sides ?? pendingJoin?.sides) : null;
  const onCode = stage === 'code';
  const sideVisible = stage === 'role' || stage === 'auth' || stage === 'name';
  const overlayOpen = stage === 'auth' || stage === 'name';
  const scenarioForArt = capacity?.scenario ?? pendingJoin?.scenario ?? null;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: '-3%',
          transform: onCode ? 'scale(1)' : leaving ? 'scale(1.10)' : 'scale(1.055)',
          transition: `transform 1.6s ${EASE}`,
        }}
      >
        <ImageSlot
          slot={SLOT.splash}
          scenario={null}
          photo
          placeholder="EXERCISE IMAGERY"
          onResolved={setSplashFound}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(90deg,rgba(4,6,9,.92) 0%,rgba(4,6,9,.80) 38%,rgba(4,6,9,.20) 72%,rgba(4,6,9,0) 100%)',
          opacity: onCode ? 1 : 0.5,
          transition: `opacity .9s ${EASE}`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: '#040609',
          opacity: onCode ? 0 : leaving ? 0.82 : 0.58,
          transition: `opacity .9s ${EASE}`,
        }}
      />

      {splashFound && (
        <div
          style={{
            position: 'absolute',
            left: 'clamp(24px,5vw,64px)',
            bottom: 10,
            fontFamily: 'var(--font-data)',
            fontSize: 9,
            letterSpacing: '.10em',
            color: 'rgba(255,255,255,.24)',
            textTransform: 'uppercase',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {SPLASH_CREDIT}
        </div>
      )}

      <div
        aria-live="polite"
        role="status"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {checking ? 'Authenticating' : `Step ${stepNumber} of 4, ${STAGE_TITLE[stage]}`}
      </div>

      <span
        data-testid="entry-step"
        style={{
          position: 'absolute',
          top: 16,
          right: 'clamp(24px,5vw,64px)',
          zIndex: 8,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0 11px',
          border: '1px solid rgba(255,255,255,.28)',
          color: 'rgba(255,255,255,.78)',
          fontFamily: 'var(--font-data)',
          fontSize: 10,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
        }}
      >
        Step {stepNumber} / 4
      </span>

      {!onCode && (
        <button
          type="button"
          onClick={back}
          style={{
            position: 'absolute',
            top: 16,
            left: 'clamp(24px,5vw,64px)',
            zIndex: 8,
            height: 28,
            padding: '0 11px',
            background: 'rgba(255,255,255,.06)',
            border: '1px solid rgba(255,255,255,.28)',
            color: 'rgba(255,255,255,.78)',
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
          }}
        >
          ← Back
        </button>
      )}

      {/* Code entry */}
      <div
        {...inertUnless(onCode)}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 'clamp(20px,4vh,40px) 0 clamp(40px,7vh,64px) clamp(24px,5vw,64px)',
          transform: onCode ? 'none' : 'translate3d(0,-34px,0) scale(.985)',
          opacity: onCode ? 1 : 0,
          pointerEvents: onCode ? 'auto' : 'none',
          transition: `transform .62s ${EASE},opacity .4s ${EASE}`,
        }}
      >
        <div style={{ maxWidth: 760 }}>
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontWeight: 700,
              fontSize: 'clamp(52px,11vh,120px)',
              lineHeight: 0.88,
              letterSpacing: '.02em',
              color: '#fff',
              textTransform: 'uppercase',
            }}
          >
            Badger
            <br />
            Sentinel
          </div>
          <div
            style={{
              height: 2,
              background: '#fff',
              width: 'clamp(260px,32vw,480px)',
              margin: 'clamp(14px,3vh,28px) 0 clamp(8px,1.4vh,14px)',
            }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              marginTop: 'clamp(20px,5vh,44px)',
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-data)',
                  fontSize: 10,
                  letterSpacing: '.18em',
                  color: 'rgba(255,255,255,.60)',
                  textTransform: 'uppercase',
                }}
              >
                Session code
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontWeight: 500,
                    fontSize: 18,
                    letterSpacing: '.06em',
                    color: 'rgba(255,255,255,.60)',
                  }}
                >
                  BSNT-
                </span>
                {codeCells.map((value, i) => (
                  <input
                    key={i}
                    data-testid={i === 0 ? 'code' : `code-${i}`}
                    ref={(el) => {
                      codeRefs.current[i] = el;
                    }}
                    value={value}
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={`Session code character ${i + 1}`}
                    onChange={(e) => setCodeCell(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void submitCode();
                      if (e.key === 'Backspace' && !codeCells[i] && i > 0)
                        codeRefs.current[i - 1]?.focus();
                    }}
                    style={{
                      flex: 'none',
                      width: 42,
                      height: 42,
                      textAlign: 'center',
                      fontSize: 18,
                      fontWeight: 500,
                      color: '#fff',
                      background: 'rgba(255,255,255,.07)',
                      border: `1px solid ${codeError ? '#ff5a5a' : 'rgba(255,255,255,.38)'}`,
                    }}
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              data-testid="code-submit"
              disabled={!codeComplete || codeChecking}
              onClick={() => void submitCode()}
              style={{
                flex: 'none',
                height: 42,
                padding: '0 20px',
                background: '#fff',
                color: '#05070a',
                border: '1px solid #fff',
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                opacity: !codeComplete || codeChecking ? 0.45 : 1,
              }}
            >
              {codeChecking ? 'Checking…' : 'Enter →'}
            </button>
          </div>

          <div style={{ minHeight: 44, marginTop: 12, maxWidth: 420 }}>
            <ErrorNote error={codeError} />
          </div>
        </div>
      </div>

      {/* Side select */}
      <div
        {...inertUnless(stage === 'role')}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 'clamp(56px,8vh,80px) clamp(24px,5vw,64px) clamp(30px,5vh,52px)',
          transform:
            stage === 'role'
              ? 'none'
              : overlayOpen
                ? 'translate3d(0,-14px,0) scale(.975)'
                : 'translate3d(0,28px,0) scale(.99)',
          opacity: stage === 'role' ? 1 : overlayOpen ? 0.5 : 0,
          pointerEvents: stage === 'role' ? 'auto' : 'none',
          transition: `transform .62s ${EASE},opacity .46s ${EASE}`,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            fontSize: 26,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: '#fff',
          }}
        >
          Select side
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 16,
            marginTop: 'clamp(12px,2.2vh,22px)',
            height: 'min(54vh,420px)',
          }}
        >
          {SIDE_ORDER.map((key, i) => {
            const side = sideOf(key, capacity?.sides);
            const cap = capacity?.capacity[key];
            const full = cap ? cap.open <= 0 : false;
            return (
              <div
                key={key}
                style={{
                  minHeight: 0,
                  overflow: 'hidden',
                  background: 'var(--panel)',
                  border: '1px solid var(--panel-edge)',
                  borderTop: `3px solid ${side.accent}`,
                  display: 'flex',
                  flexDirection: 'column',
                  transform: sideVisible ? 'none' : 'translate3d(0,26px,0)',
                  opacity: sideVisible ? 1 : 0,
                  transition: `transform .6s ${EASE},opacity .44s ${EASE}`,
                  transitionDelay: stage === 'role' ? `${90 + i * 80}ms` : '0ms',
                }}
              >
                <div
                  style={{
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: 'clamp(10px,1.8vh,18px) 16px 0',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: 'var(--font-ui)',
                        fontWeight: 700,
                        fontSize: 24,
                        letterSpacing: '.10em',
                        textTransform: 'uppercase',
                        color: side.accent,
                      }}
                    >
                      {side.name}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 10,
                        lineHeight: 1.4,
                        letterSpacing: '.12em',
                        color: 'var(--text-2)',
                        textTransform: 'uppercase',
                        marginTop: 8,
                        maxWidth: 170,
                      }}
                    >
                      {side.identity}
                    </div>
                  </div>
                  {cap && (
                    <span
                      style={{
                        flex: 'none',
                        fontFamily: 'var(--font-data)',
                        fontSize: 10,
                        letterSpacing: '.14em',
                        textTransform: 'uppercase',
                        border: `1px solid ${full ? 'var(--warn)' : 'var(--ok)'}`,
                        color: full ? 'var(--warn)' : 'var(--ok)',
                        padding: '3px 7px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {full ? 'FULL' : 'OPEN'}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'clamp(6px,1.4vh,14px) 16px',
                  }}
                >
                  <div
                    style={{
                      width: 'min(120px,100%)',
                      height: '100%',
                      maxHeight: 120,
                      aspectRatio: '1',
                      background: 'var(--panel-2)',
                      border: '1px solid var(--panel-edge)',
                    }}
                  >
                    <ImageSlot
                      slot={side.crestSlot}
                      scenario={scenarioForArt}
                      placeholder="PATCH"
                    />
                  </div>
                </div>
                <div
                  style={{
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    padding: '0 16px clamp(10px,1.8vh,16px)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 10,
                      letterSpacing: '.14em',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Seats open
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 20,
                      color: full ? 'var(--text-disabled)' : 'var(--text)',
                    }}
                  >
                    {cap ? cap.open : '—'}
                    <span style={{ fontSize: 13, color: 'var(--text-disabled)' }}>
                      {cap ? ` / ${cap.total}` : ''}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  data-testid={`role-${key}`}
                  ref={(el) => {
                    roleRefs.current[key] = el;
                  }}
                  onClick={() => pickRole(key)}
                  style={{
                    flex: 'none',
                    height: 46,
                    border: 0,
                    borderTop: '1px solid var(--panel-edge)',
                    background: 'transparent',
                    color: side.accent,
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                  }}
                >
                  <span>{full ? 'Full — join anyway' : 'Take'}</span>
                  <span>→</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Overlay scrim behind auth/name */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          background: 'rgba(4,6,9,.55)',
          opacity: overlayOpen ? 1 : 0,
          pointerEvents: overlayOpen ? 'auto' : 'none',
          transition: `opacity .44s ${EASE}`,
        }}
      />

      {/* Auth / name panel */}
      <div
        {...inertUnless(overlayOpen && !leaving)}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(56px,8vh,80px) 20px 30px',
          pointerEvents: overlayOpen && !leaving ? 'auto' : 'none',
          transform: overlayOpen ? 'none' : 'translate3d(0,26px,0) scale(.985)',
          opacity: overlayOpen ? 1 : 0,
          transition: `transform .56s ${EASE},opacity .4s ${EASE}`,
        }}
      >
        {picked && (
          <div
            style={{
              width: 560,
              maxWidth: '100%',
              maxHeight: '100%',
              overflow: 'auto',
              background: 'var(--panel)',
              border: '1px solid var(--panel-edge)',
              borderTop: `3px solid ${picked.accent}`,
            }}
          >
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div
                  style={{
                    flex: 'none',
                    width: 84,
                    height: 84,
                    background: 'var(--panel-2)',
                    border: '1px solid var(--panel-edge)',
                  }}
                >
                  <ImageSlot
                    slot={picked.crestSlot}
                    scenario={scenarioForArt}
                    placeholder="PATCH"
                  />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontWeight: 700,
                      fontSize: 26,
                      letterSpacing: '.08em',
                      textTransform: 'uppercase',
                      color: picked.accent,
                    }}
                  >
                    {picked.name}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 10,
                      letterSpacing: '.12em',
                      color: 'var(--text-2)',
                      textTransform: 'uppercase',
                      marginTop: 8,
                    }}
                  >
                    {picked.identity}
                  </div>
                </div>
                <span
                  style={{
                    flex: 'none',
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    border: '1px solid var(--ok)',
                    color: 'var(--ok)',
                    padding: '3px 8px',
                    fontFamily: 'var(--font-data)',
                    fontSize: 10,
                    letterSpacing: '.12em',
                    textTransform: 'uppercase',
                    opacity: stage === 'name' ? 1 : 0,
                    transition: `opacity .4s ${EASE}`,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      background: 'var(--ok)',
                      display: 'inline-block',
                    }}
                  />
                  Authenticated
                </span>
              </div>

              {/* Both steps stay mounted and slide past each other. A hard swap
                  would cut the panel's height and its content in the same frame,
                  which reads as a glitch rather than as a step forward. */}
              <div
                style={{
                  position: 'relative',
                  marginTop: 22,
                  height: bodyHeight,
                  overflow: 'hidden',
                  transition: `height .5s ${EASE}`,
                }}
              >
                <div
                  ref={authBodyRef}
                  {...inertUnless(stage === 'auth')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: stage === 'name' ? 'translate3d(-22px,0,0)' : 'none',
                    opacity: stage === 'name' ? 0 : 1,
                    pointerEvents: stage === 'auth' ? 'auto' : 'none',
                    transition: `transform .46s ${EASE},opacity .28s ${EASE}`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 12,
                      fontFamily: 'var(--font-data)',
                      fontSize: 10,
                      letterSpacing: '.18em',
                      color: 'var(--text-3)',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span>Cell passphrase</span>
                    {attemptsLeft !== null && (
                      <span data-testid="attempts-left" style={{ color: 'var(--warn)' }}>
                        Attempts left · {attemptsLeft}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    {words.map((value, i) => (
                      <input
                        key={i}
                        data-testid={i === 0 ? 'password' : `password-${i}`}
                        ref={(el) => {
                          wordRefs.current[i] = el;
                        }}
                        value={value}
                        disabled={checking}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label={`Passphrase word ${i + 1}`}
                        onChange={(e) => setWordCell(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void submitAuth();
                          if (e.key === ' ') {
                            e.preventDefault();
                            wordRefs.current[i + 1]?.focus();
                          }
                          if (e.key === 'Backspace' && !words[i] && i > 0)
                            wordRefs.current[i - 1]?.focus();
                        }}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: 48,
                          padding: '0 10px',
                          fontSize: 15,
                          letterSpacing: '.04em',
                          textTransform: 'uppercase',
                          borderColor: authError ? 'var(--red)' : undefined,
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ minHeight: 44, marginTop: 12 }}>
                    {checking ? (
                      <div
                        style={{
                          border: '1px solid var(--panel-edge)',
                          padding: '9px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            background: 'var(--warn-dot)',
                            display: 'inline-block',
                          }}
                        />
                        <span
                          style={{
                            fontFamily: 'var(--font-data)',
                            fontSize: 11,
                            letterSpacing: '.12em',
                            color: 'var(--text-2)',
                            textTransform: 'uppercase',
                          }}
                        >
                          Authenticating…
                        </span>
                      </div>
                    ) : (
                      <ErrorNote error={authError} />
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                    <button type="button" onClick={back} style={secondaryButton()}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="password-submit"
                      disabled={checking || !wordsComplete}
                      onClick={() => void submitAuth()}
                      style={{ ...primaryButton(checking || !wordsComplete), flex: 1 }}
                    >
                      {checking ? 'Authenticating…' : 'Authenticate →'}
                    </button>
                  </div>
                </div>

                <div
                  ref={nameBodyRef}
                  {...inertUnless(stage === 'name' && !leaving)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: stage === 'name' ? 'none' : 'translate3d(22px,0,0)',
                    opacity: stage === 'name' ? 1 : 0,
                    pointerEvents: stage === 'name' && !leaving ? 'auto' : 'none',
                    transition: `transform .46s ${EASE},opacity .28s ${EASE}`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 10,
                      letterSpacing: '.18em',
                      color: 'var(--text-3)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Display name (optional)
                  </div>
                  <input
                    ref={nameRef}
                    data-testid="display-name"
                    value={displayName}
                    maxLength={40}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label="Display name"
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') enterLobby();
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      height: 50,
                      marginTop: 10,
                      padding: '0 12px',
                      fontSize: 15,
                      letterSpacing: '.02em',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <button
                      type="button"
                      onClick={() => setStage('role')}
                      style={secondaryButton()}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="enter-lobby"
                      onClick={enterLobby}
                      style={{ ...primaryButton(false), flex: 1 }}
                    >
                      Enter lobby →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function primaryButton(disabled: boolean): React.CSSProperties {
  return {
    height: 50,
    background: disabled ? 'var(--surface-2)' : 'var(--primary)',
    color: disabled ? 'var(--text-disabled)' : 'var(--primary-on)',
    border: `1px solid ${disabled ? 'var(--border)' : 'var(--primary)'}`,
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '.16em',
    textTransform: 'uppercase',
  };
}

function secondaryButton(): React.CSSProperties {
  return {
    height: 50,
    width: 140,
    flex: 'none',
    background: 'transparent',
    border: '1px solid var(--border-strong)',
    color: 'var(--text-2)',
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: '.16em',
    textTransform: 'uppercase',
  };
}
